import React, { useState, useRef } from "react";
import { Navigation } from "../components/Navigation";
import { COPY } from "../constants/copy";
import { OTP_CORRECT } from "../constants/config";

interface UnlockSectionProps {
  onSuccess: () => void;
  onPrevious: () => void;
}

export const UnlockSection: React.FC<UnlockSectionProps> = ({
  onSuccess,
  onPrevious,
}) => {
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleSubmit = () => {
    if (otp === OTP_CORRECT) {
      onSuccess();
    } else {
      setError(COPY.unlock.wrongAttempt);
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setOtp("");
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-warmWhite dark:bg-darkBg transition-colors duration-400">
      <div
        className={`max-w-md text-center space-y-8 ${
          shake ? "animate-shake" : ""
        }`}
      >
        <div className="space-y-2">
          <h2 className="text-3xl font-bold text-deepCharcoal dark:text-darkText">
            {COPY.unlock.title}
          </h2>
          <p className="text-mutedGray dark:text-gray-400">
            {COPY.unlock.hint}
          </p>
        </div>

        <div className="space-y-4">
          <div className="flex justify-center gap-3">
            {[0, 1, 2, 3].map((i) => (
              <input
                ref={(el) => {
                  inputRefs.current[i] = el;
                }}
                key={i}
                type="password"
                inputMode="numeric"
                maxLength={1}
                value={otp[i] || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^[0-9]$/.test(value)) {
                    const newOtp = otp.split("");
                    newOtp[i] = value;
                    setOtp(newOtp.join(""));
                    setError("");
                    // Auto-focus next input
                    if (i < 3) {
                      inputRefs.current[i + 1]?.focus();
                    }
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Backspace") {
                    e.preventDefault();
                    if (otp[i]) {
                      // Delete current character
                      const newOtp = otp.split("");
                      newOtp[i] = "";
                      setOtp(newOtp.join(""));
                    } else if (i > 0) {
                      // Move to previous and delete
                      const newOtp = otp.split("");
                      newOtp[i - 1] = "";
                      setOtp(newOtp.join(""));
                      inputRefs.current[i - 1]?.focus();
                    }
                  } else if (e.key === "ArrowLeft" && i > 0) {
                    e.preventDefault();
                    inputRefs.current[i - 1]?.focus();
                  } else if (e.key === "ArrowRight" && i < 3) {
                    e.preventDefault();
                    inputRefs.current[i + 1]?.focus();
                  }
                }}
                className="w-16 h-16 text-center text-2xl font-bold rounded-lg border-2 border-softRose dark:border-gray-600 bg-blushPink dark:bg-darkCard text-deepCharcoal dark:text-darkText focus:outline-none focus:border-heartRed transition-colors duration-300"
              />
            ))}
          </div>

          {error && (
            <p className="text-heartRed text-sm font-medium animate-fade-in">
              {error}
            </p>
          )}
        </div>

        <Navigation
          onNext={handleSubmit}
          onPrevious={onPrevious}
          nextLabel="Unlock"
          showPrevious={true}
        />
      </div>
    </div>
  );
};
